import { useEffect, useState } from 'react';

export type ElapsedTimeLabels = Partial<Record<'minute' | 'minutes' | 'hour' | 'hours' | 'day' | 'days' | 'daysMore', string>>;

// How often (ms) the relative-time label is recomputed while mounted.
const REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Pure formatter: turns an ISO date string into a human relative-time label
 * (e.g. "5 minutes ago"), using authored labels when provided.
 */
// Replaces the count placeholder in an authored label. Supports both the
// single-brace `{count}` default and the double-brace `{{count}}` AEM
// placeholder convention, anywhere in the string.
function withCount(template: string, value: number): string {
    return template.replace(/\{{1,2}count}{1,2}/g, String(value));
}

export function formatRelativeTime(dateStr: string, labels: ElapsedTimeLabels = {}): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return labels.minute ?? '1 minute ago';
    if (minutes < 60) return withCount(labels.minutes ?? '{count} minutes ago', minutes);
    if (hours === 1) return labels.hour ?? '1 hour ago';
    if (hours < 24) return withCount(labels.hours ?? '{count} hours ago', hours);
    if (days === 1) return labels.day ?? '1 day ago';
    if (days > 30) return labels.daysMore ?? '30+ days ago';
    return withCount(labels.days ?? '{count} days ago', days);
}

/**
 * Returns a live relative-time label for `createdAt` that refreshes itself every
 * minute so the displayed value stays current without a parent re-render.
 */
export function useRelativeTime(createdAt: string, labels: ElapsedTimeLabels = {}): string {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick(x => x + 1), REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [createdAt]);
    return formatRelativeTime(createdAt, labels);
}
